import { Tool, Employee, Notification, AppSettings, TrainingRecord, GovernanceTask, Curriculum, CurriculumCompletion, AuditLogEntry, ToolLimitResult, CompanyBilling, PlanUsage, PlanLimits, DEFAULT_CRITICALITY_LEVELS, CriticalityLevel, ToolHistoryEntry, FeatureRequest } from '../types';
import { supabase } from './supabaseClient';

const ACCESS_CHANGE_REQUEST_MARKER = 'ACCESS_CHANGE_REQUEST';

function handleSupabaseError(error: unknown, context: string): asserts error is null {
  if (error) {
    const msg = error instanceof Error ? error.message : typeof error === 'object' && error !== null && 'message' in error ? String((error as { message: unknown }).message) : String(error);
    throw new Error(`Supabase error (${context}): ${msg}`);
  }
}

// ==========================================
// Generic CRUD helper
// ==========================================
interface CrudConfig<T, SqlRow> {
  table: string;
  fromSql: (row: SqlRow) => T;
  toSql: (item: T, companyId: string) => Record<string, unknown>;
  orderBy?: { column: string; ascending: boolean };
}

function createCrud<T extends { id: string }>(config: CrudConfig<T, Record<string, unknown>>) {
  const { table, fromSql, toSql, orderBy } = config;

  async function getAll(companyId: string): Promise<T[]> {
    let query = supabase.from(table).select('*').eq('company_id', companyId);
    if (orderBy) query = query.order(orderBy.column, { ascending: orderBy.ascending });
    const { data, error } = await query;
    handleSupabaseError(error, `get:${table}`);
    return data ? data.map(fromSql) : [];
  }

  async function add(item: T, companyId: string): Promise<T[]> {
    const row = toSql(item, companyId);
    const payload = item.id ? { ...row, id: item.id } : row;
    const { error } = await supabase.from(table).insert([payload]);
    handleSupabaseError(error, `add:${table}`);
    return getAll(companyId);
  }

  async function update(item: T, companyId: string): Promise<T[]> {
    const { error } = await supabase
      .from(table)
      .update(toSql(item, companyId))
      .eq('id', item.id)
      .eq('company_id', companyId);
    handleSupabaseError(error, `update:${table}`);
    return getAll(companyId);
  }

  async function remove(id: string, companyId: string): Promise<T[]> {
    const { error } = await supabase
      .from(table)
      .delete()
      .eq('id', id)
      .eq('company_id', companyId);
    handleSupabaseError(error, `delete:${table}`);
    return getAll(companyId);
  }

  return { getAll, add, update, remove };
}

// ==========================================
// 1. TOOLS
// ==========================================
function sqlToTool(row: any): Tool {
  return {
    id: row.id,
    name: row.name,
    category: row.category,
    importance: row.importance,
    pricingModel: row.pricing_model,
    basePrice: row.base_price ?? 0,
    userCount: row.user_count ?? 0,
    monthlyCost: row.monthly_cost ?? 0,
    currency: typeof row.currency === 'string' ? row.currency : undefined,
    complexity: row.complexity ?? 1,
    teamLiteracy: row.team_literacy ?? 5,
    renewalDate: row.renewal_date || '',
    noticeDeadline: row.notice_deadline || '',
    satisfactionScore: row.satisfaction_score ?? 0,
    description: row.description ?? undefined,
    website: row.website_url || '',
    notes: row.notes ?? undefined,
    teams: Array.isArray(row.teams) ? row.teams : (row.team ? [row.team] : undefined),
    powerUserId: row.power_user_id || '',
    assignedUserIds: row.assigned_user_ids ?? [],
    tags: row.tags ?? [],
    feedback: row.feedback ?? [],
    trainingUrl: row.training_url ?? '',
    nextTrainingDate: row.next_training_date ?? '',
    vendorContactName: row.vendor_contact_name ?? undefined,
    vendorContactEmail: row.vendor_contact_email ?? undefined,
    vendorSupportUrl: row.vendor_support_url ?? undefined,
    vendorPortalUrl: row.vendor_portal_url ?? undefined,
    contractNumber: row.contract_number ?? undefined,
    accountManager: row.account_manager ?? undefined,
  };
}

function toolToSql(tool: Tool, companyId: string): any {
  return {
    name: tool.name,
    category: tool.category,
    importance: tool.importance,
    pricing_model: tool.pricingModel,
    base_price: tool.basePrice,
    user_count: tool.userCount,
    monthly_cost: tool.monthlyCost,
    currency: tool.currency ?? null,
    complexity: tool.complexity,
    team_literacy: tool.teamLiteracy,
    renewal_date: tool.renewalDate,
    notice_deadline: tool.noticeDeadline,
    satisfaction_score: tool.satisfactionScore,
    description: tool.description ?? null,
    website_url: tool.website,
    notes: tool.notes ?? null,
    teams: (tool.teams?.length ? tool.teams : null) ?? null,
    power_user_id: tool.powerUserId,
    assigned_user_ids: tool.assignedUserIds,
    tags: tool.tags,
    feedback: tool.feedback,
    training_url: tool.trainingUrl,
    next_training_date: tool.nextTrainingDate,
    vendor_contact_name: tool.vendorContactName ?? null,
    vendor_contact_email: tool.vendorContactEmail ?? null,
    vendor_support_url: tool.vendorSupportUrl ?? null,
    vendor_portal_url: tool.vendorPortalUrl ?? null,
    contract_number: tool.contractNumber ?? null,
    account_manager: tool.accountManager ?? null,
    company_id: companyId,
  };
}

const toolsCrud = createCrud<Tool>({ table: 'tools', fromSql: sqlToTool, toSql: toolToSql });
export const getTools = toolsCrud.getAll;
export const updateTool = toolsCrud.update;
export const deleteTool = toolsCrud.remove;

export async function checkToolLimit(companyId: string): Promise<ToolLimitResult> {
  const { count, error: countErr } = await supabase
    .from('tools')
    .select('id', { count: 'exact', head: true })
    .eq('company_id', companyId);
  handleSupabaseError(countErr, 'checkToolLimit:count');

  const { data: company, error: companyErr } = await supabase
    .from('companies')
    .select('plan_limits')
    .eq('id', companyId)
    .single();
  handleSupabaseError(companyErr, 'checkToolLimit:company');

  const maxTools = company?.plan_limits?.max_tools ?? 25;
  const current = count ?? 0;

  return {
    allowed: current < maxTools,
    current,
    limit: maxTools,
  };
}

export async function addTool(tool: Tool, companyId: string): Promise<Tool[]> {
  const limit = await checkToolLimit(companyId);
  if (!limit.allowed) {
    throw new Error(`Tool limit reached (${limit.current}/${limit.limit}). Upgrade your plan to add more tools.`);
  }
  return toolsCrud.add(tool, companyId);
}

// ==========================================
// 2. EMPLOYEES
// ==========================================
function sqlToEmployee(row: any): Employee {
  const firstName = (row.first_name ?? '').trim();
  const lastName = (row.last_name ?? '').trim();
  const combined = [firstName, lastName].filter(Boolean).join(' ').trim();
  return {
    id: row.id,
    name: combined || row.name || 'Unknown',
    firstName: firstName || undefined,
    lastName: lastName || undefined,
    role: row.role ?? 'Employee',
    email: row.email ?? '',
    team: row.team ?? row.department ?? '',
    managerId: row.manager_id ?? row.direct_manager_id ?? undefined,
    authRole: row.auth_role ?? 'user',
    userId: row.user_id ?? undefined,
    invitedAt: row.invited_at ?? undefined,
    lastLoginAt: row.last_login_at ?? undefined,
    companyId: row.company_id ?? undefined,
  };
}

function employeeToSql(emp: Employee, companyId: string): any {
  const firstName = (emp.firstName ?? '').trim();
  const lastName = (emp.lastName ?? '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim() || emp.name;
  return {
    name: fullName,
    first_name: firstName,
    last_name: lastName,
    role: emp.role,
    email: emp.email,
    department: emp.team ?? '',
    direct_manager_id: emp.managerId || null,
    auth_role: emp.authRole ?? 'user',
    company_id: companyId,
  };
}

export async function getEmployeeByUserId(userId: string): Promise<Employee | null> {
  const { data, error } = await supabase.from('employees').select('*').eq('user_id', userId).maybeSingle();
  handleSupabaseError(error, 'getEmployeeByUserId');
  return data ? sqlToEmployee(data) : null;
}

export async function linkEmployeeToAuthUser(employeeId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('employees')
    .update({ user_id: userId, invited_at: new Date().toISOString() })
    .eq('id', employeeId);
  handleSupabaseError(error, 'linkEmployeeToAuthUser');
}

export async function getAdminCount(companyId: string): Promise<number> {
  const { count, error } = await supabase
    .from('employees')
    .select('id', { count: 'exact', head: true })
    .eq('auth_role', 'admin')
    .eq('company_id', companyId);
  handleSupabaseError(error, 'getAdminCount');
  return count ?? 0;
}

const employeesCrud = createCrud<Employee>({ table: 'employees', fromSql: sqlToEmployee, toSql: employeeToSql });
export const getEmployees = employeesCrud.getAll;
export const addEmployee = employeesCrud.add;
export const updateEmployee = employeesCrud.update;
export const deleteEmployee = employeesCrud.remove;

// ==========================================
// 3. NOTIFICATIONS
// ==========================================
function sqlToNotification(row: any): Notification {
  const requestKind: 'new' | 'change' | undefined =
    row.request_kind === 'change' || row.message === ACCESS_CHANGE_REQUEST_MARKER ? 'change' :
    row.request_kind === 'new' ? 'new' : undefined;

  return {
    id: row.id,
    type: row.type as any,
    status: row.status as any,
    requestKind,
    requesterId: row.requester_id,
    requesterName: row.requester_name,
    createdAt: row.created_at,
    toolId: row.tool_id,
    toolName: row.tool_name,
    accessReason: row.access_reason,
    accessNeededBy: row.access_needed_by,
    trainingNeeds: row.training_needs,
    newToolData: row.new_tool_data,
    message: row.message ?? undefined,
    targetUserId: row.target_user_id ?? undefined,
    reviewData: row.review_data ?? undefined,
    history: Array.isArray(row.history) ? row.history : undefined,
  };
}

function notifToSql(n: Notification, companyId: string): any {
  const legacyMessage =
    n.type === 'ACCESS_REQUEST' && n.requestKind === 'change'
      ? ACCESS_CHANGE_REQUEST_MARKER
      : (n.message ?? null);

  return {
    type: n.type,
    status: n.status,
    requester_id: n.requesterId,
    requester_name: n.requesterName,
    created_at: n.createdAt,
    tool_id: n.toolId,
    tool_name: n.toolName,
    access_reason: n.accessReason,
    access_needed_by: n.accessNeededBy,
    training_needs: n.trainingNeeds,
    new_tool_data: n.newToolData,
    message: legacyMessage,
    target_user_id: n.targetUserId ?? null,
    company_id: companyId,
  };
}

const notifsCrud = createCrud<Notification>({ table: 'notifications', fromSql: sqlToNotification, toSql: notifToSql, orderBy: { column: 'created_at', ascending: false } });
export const getNotifications = notifsCrud.getAll;
export const addNotification = notifsCrud.add;
export const updateNotification = notifsCrud.update;

// ==========================================
// 4. TRAINING RECORDS
// ==========================================
function sqlToTraining(row: any): TrainingRecord {
  return {
    id: row.id,
    employeeId: row.employee_id,
    toolId: row.tool_id,
    completedAt: row.completed_at,
    satisfaction: row.satisfaction,
    proficiency: row.proficiency,
  };
}

export async function getTrainingRecords(companyId: string): Promise<TrainingRecord[]> {
  const { data, error } = await supabase.from('training_records').select('*').eq('company_id', companyId);
  handleSupabaseError(error, 'getTrainingRecords');
  return data ? data.map(sqlToTraining) : [];
}

export async function completeTrainingRecord(
  employeeId: string,
  toolId: string,
  satisfaction: number,
  proficiency: number,
  companyId: string
): Promise<TrainingRecord[]> {
  const record: any = {
    employee_id: employeeId,
    tool_id: toolId,
    completed_at: new Date().toISOString(),
    satisfaction,
    proficiency,
    company_id: companyId,
  };
  const { error } = await supabase.from('training_records').insert([record]);
  handleSupabaseError(error, 'completeTraining');
  return getTrainingRecords(companyId);
}

// ==========================================
// 4b. CURRICULA
// ==========================================
function sqlToCurriculum(row: any): Curriculum {
  return {
    id: row.id,
    name: row.name ?? '',
    url: row.url ?? undefined,
    toolId: row.tool_id,
    createdAt: row.created_at ?? undefined,
    nextDate: row.next_date ?? undefined,
    description: row.description ?? undefined,
    audience: row.audience === 'admin' ? 'admin' : 'user',
  };
}

function curriculumToSql(c: Curriculum, companyId: string): any {
  return {
    id: c.id || crypto.randomUUID(),
    name: c.name,
    url: c.url ?? null,
    tool_id: c.toolId,
    next_date: c.nextDate ?? null,
    description: c.description ?? null,
    audience: c.audience === 'admin' ? 'admin' : 'user',
    company_id: companyId,
  };
}

const curriculaCrud = createCrud<Curriculum>({ table: 'curricula', fromSql: sqlToCurriculum, toSql: curriculumToSql });
export const getCurricula = curriculaCrud.getAll;
export async function addCurriculum(c: Curriculum, companyId: string): Promise<Curriculum[]> {
  return curriculaCrud.add({ ...c, id: c.id || crypto.randomUUID() }, companyId);
}
export const updateCurriculum = curriculaCrud.update;
export const deleteCurriculum = curriculaCrud.remove;

// ==========================================
// 4c. CURRICULUM COMPLETIONS
// ==========================================
function sqlToCurriculumCompletion(row: any): CurriculumCompletion {
  return {
    id: row.id,
    employeeId: row.employee_id,
    curriculumId: row.curriculum_id,
    completedAt: row.completed_at ?? new Date().toISOString(),
    satisfaction: row.satisfaction != null ? Number(row.satisfaction) : undefined,
    proficiency: row.proficiency != null ? Number(row.proficiency) : undefined,
  };
}

export async function getCurriculumCompletions(companyId: string): Promise<CurriculumCompletion[]> {
  const { data, error } = await supabase.from('curriculum_completions').select('*').eq('company_id', companyId);
  handleSupabaseError(error, 'getCurriculumCompletions');
  return data ? data.map(sqlToCurriculumCompletion) : [];
}

export async function addCurriculumCompletion(
  employeeId: string,
  curriculumId: string,
  satisfaction: number,
  companyId: string
): Promise<CurriculumCompletion[]> {
  const row: any = {
    id: crypto.randomUUID(),
    employee_id: employeeId,
    curriculum_id: curriculumId,
    completed_at: new Date().toISOString(),
    satisfaction,
    proficiency: null,
    company_id: companyId,
  };
  const { error } = await supabase.from('curriculum_completions').insert([row]);
  handleSupabaseError(error, 'addCurriculumCompletion');
  return getCurriculumCompletions(companyId);
}

// ==========================================
// 5. GOVERNANCE
// ==========================================
function sqlToGov(row: any): GovernanceTask {
  return {
    id: row.id,
    title: row.title ?? '',
    date: row.date ?? row.due_date,
    dueDate: row.due_date,
    toolId: row.tool_id,
    assigneeId: row.assignee_id,
    status: row.status,
    priority: row.priority,
    isCompleted: row.is_completed ?? false,
  };
}

function govToSql(task: GovernanceTask, companyId: string): any {
  return {
    title: task.title,
    tool_id: task.toolId,
    assignee_id: task.assigneeId,
    due_date: task.dueDate,
    status: task.status,
    priority: task.priority,
    is_completed: task.isCompleted,
    company_id: companyId,
  };
}

const govCrud = createCrud<GovernanceTask>({ table: 'governance_tasks', fromSql: sqlToGov, toSql: govToSql });
export const getGovernanceTasks = govCrud.getAll;
export const addGovernanceTask = govCrud.add;
export const updateGovernanceTask = govCrud.update;
export const deleteGovernanceTask = govCrud.remove;

export async function toggleGovernanceTask(id: string, companyId: string): Promise<GovernanceTask[]> {
  const { data, error } = await supabase
    .from('governance_tasks')
    .select('is_completed')
    .eq('id', id)
    .eq('company_id', companyId)
    .single();
  handleSupabaseError(error, 'toggleGovernanceTask:select');
  if (data) {
    const { error: updateError } = await supabase
      .from('governance_tasks')
      .update({ is_completed: !data.is_completed })
      .eq('id', id)
      .eq('company_id', companyId);
    handleSupabaseError(updateError, 'toggleGovernanceTask:update');
  }
  return getGovernanceTasks(companyId);
}

// ==========================================
// 5b. AUDIT LOG
// ==========================================
function sqlToAuditEntry(row: any): AuditLogEntry {
  return {
    id: row.id,
    createdAt: row.created_at,
    eventType: row.event_type === 'GOVERNANCE_TASK_COMPLETED' ? 'GOVERNANCE_TASK_COMPLETED' : 'ACCESS_TASK_CLOSED',
    message: row.message ?? '',
    notificationId: row.notification_id,
    toolName: row.tool_name,
    requesterName: row.requester_name,
    newStatus: row.new_status ?? '',
  };
}

export async function getAuditLog(companyId: string): Promise<AuditLogEntry[]> {
  const { data, error } = await supabase
    .from('audit_log')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });
  handleSupabaseError(error, 'getAuditLog');
  return data ? data.map(sqlToAuditEntry) : [];
}

export async function addAuditLogEntry(
  entry: Omit<AuditLogEntry, 'id' | 'createdAt'>,
  companyId: string
): Promise<AuditLogEntry[]> {
  const row: any = {
    id: crypto.randomUUID(),
    event_type: entry.eventType,
    message: entry.message,
    notification_id: entry.notificationId ?? null,
    tool_name: entry.toolName ?? null,
    requester_name: entry.requesterName ?? null,
    new_status: entry.newStatus,
    company_id: companyId,
  };
  const { error } = await supabase.from('audit_log').insert([row]);
  handleSupabaseError(error, 'addAuditLogEntry');
  return getAuditLog(companyId);
}

// ==========================================
// 6. SETTINGS (one row per company)
// ==========================================
function sqlToSettings(row: any): AppSettings {
  const toNum = (v: any, def: number) => (v != null && v !== '' ? Number(v) : def);
  const toArr = (v: any): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  const rates = row.currency_rates;
  const currencyRates = Array.isArray(rates)
    ? rates.filter((r: any) => r && typeof r.code === 'string').map((r: any) => ({
        code: r.code,
        label: typeof r.label === 'string' ? r.label : r.code,
        symbol: typeof r.symbol === 'string' ? r.symbol : r.code,
        rateToMain: typeof r.rateToMain === 'number' && r.rateToMain > 0 ? r.rateToMain : 1,
      }))
    : undefined;
  const rawLevels = row.criticality_levels;
  const criticalityLevels: CriticalityLevel[] = Array.isArray(rawLevels) && rawLevels.length > 0
    ? rawLevels.filter((l: any) => l && typeof l.id === 'string').map((l: any) => ({
        id: l.id,
        label: typeof l.label === 'string' ? l.label : l.id,
        color: typeof l.color === 'string' ? l.color : 'gray',
        icon: typeof l.icon === 'string' ? l.icon : 'HelpCircle',
        reviewFrequencyMonths: typeof l.reviewFrequencyMonths === 'number' ? l.reviewFrequencyMonths : 6,
        governancePriority: ['HIGH', 'MEDIUM', 'LOW'].includes(l.governancePriority) ? l.governancePriority : 'MEDIUM',
        sortOrder: typeof l.sortOrder === 'number' ? l.sortOrder : 99,
      }))
    : DEFAULT_CRITICALITY_LEVELS;

  return {
    currency: typeof row.currency === 'string' ? row.currency : 'EUR',
    currencySymbol: typeof row.currency_symbol === 'string' ? row.currency_symbol : '€',
    currencyRates: currencyRates?.length ? currencyRates : undefined,
    renewalWarningDays: toNum(row.renewal_warning_days, 30) || 30,
    noticeWarningDays: toNum(row.notice_warning_days, 60) || 60,
    categories: toArr(row.categories),
    tags: toArr(row.tags),
    teams: toArr(row.teams),
    allowedEmailDomains: toArr(row.allowed_email_domains),
    criticalityLevels,
    onboardingCompleted: row.onboarding_completed === true,
    activationChecklist: row.activation_checklist && typeof row.activation_checklist === 'object'
      ? {
          dismissed: row.activation_checklist.dismissed === true,
          items: {
            add_first_tool: row.activation_checklist.items?.add_first_tool === true,
            add_first_employee: row.activation_checklist.items?.add_first_employee === true,
            create_first_training: row.activation_checklist.items?.create_first_training === true,
            configure_categories: row.activation_checklist.items?.configure_categories === true,
            create_first_team: row.activation_checklist.items?.create_first_team === true,
            assign_tool_to_employee: row.activation_checklist.items?.assign_tool_to_employee === true,
            complete_first_governance_task: row.activation_checklist.items?.complete_first_governance_task === true,
          },
        }
      : undefined,
  };
}

function settingsToSql(settings: AppSettings): any {
  return {
    currency: settings.currency,
    currency_symbol: settings.currencySymbol,
    currency_rates: settings.currencyRates?.length ? settings.currencyRates : null,
    renewal_warning_days: settings.renewalWarningDays,
    notice_warning_days: settings.noticeWarningDays,
    categories: settings.categories,
    tags: settings.tags,
    teams: settings.teams,
    allowed_email_domains: settings.allowedEmailDomains?.length ? settings.allowedEmailDomains : null,
    criticality_levels: settings.criticalityLevels?.length ? settings.criticalityLevels : null,
    onboarding_completed: settings.onboardingCompleted ?? false,
    activation_checklist: settings.activationChecklist ?? null,
  };
}

const DEFAULT_SETTINGS: AppSettings = {
  currency: 'EUR',
  currencySymbol: '€',
  renewalWarningDays: 30,
  noticeWarningDays: 60,
  categories: ['Saas Manager'],
  tags: ['Governance', 'Training'],
  teams: [],
  allowedEmailDomains: [],
  criticalityLevels: DEFAULT_CRITICALITY_LEVELS,
  onboardingCompleted: false,
};

export async function getSettings(companyId: string): Promise<AppSettings> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('*')
    .eq('company_id', companyId)
    .limit(1)
    .maybeSingle();
  if (error || !data) return DEFAULT_SETTINGS;
  return sqlToSettings(data);
}

export async function updateCompanyIndustry(companyId: string, industry: string): Promise<void> {
  const { error } = await supabase.from('companies').update({ industry }).eq('id', companyId);
  handleSupabaseError(error, 'updateCompanyIndustry');
}

// ==========================================
// BILLING
// ==========================================

const DEFAULT_PLAN_LIMITS: PlanLimits = {
  max_tools: 25,
  ai_queries_included: 5,
  ai_overage_rate: 0,
  audit_retention_days: 30,
  approval_workflow_levels: 0,
  has_discovery: false,
  has_api: false,
};

export async function getCompanyBilling(companyId: string): Promise<CompanyBilling | null> {
  const { data, error } = await supabase
    .from('companies')
    .select('id, name, plan, paddle_customer_id, paddle_subscription_id, paddle_subscription_status, billing_email, trial_ends_at, ai_spending_cap, plan_limits')
    .eq('id', companyId)
    .single();
  if (error || !data) return null;

  return {
    id: data.id,
    name: data.name,
    plan: data.plan ?? 'trial',
    paddleCustomerId: data.paddle_customer_id,
    paddleSubscriptionId: data.paddle_subscription_id,
    paddleSubscriptionStatus: data.paddle_subscription_status ?? 'none',
    billingEmail: data.billing_email,
    trialEndsAt: data.trial_ends_at,
    aiSpendingCap: Number(data.ai_spending_cap) || 15,
    planLimits: data.plan_limits ?? DEFAULT_PLAN_LIMITS,
  };
}

export async function getCurrentPlanUsage(companyId: string): Promise<PlanUsage | null> {
  const { data, error } = await supabase
    .from('plan_usage')
    .select('*')
    .eq('company_id', companyId)
    .order('period_start', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;

  return {
    periodStart: data.period_start,
    periodEnd: data.period_end,
    toolsCount: data.tools_count ?? 0,
    aiQueriesUsed: data.ai_queries_used ?? 0,
    aiQueriesOverage: data.ai_queries_overage ?? 0,
    aiOverageAmount: Number(data.ai_overage_amount) || 0,
  };
}

export async function updateAiSpendingCap(companyId: string, cap: number): Promise<void> {
  const { error } = await supabase
    .from('companies')
    .update({ ai_spending_cap: cap })
    .eq('id', companyId);
  handleSupabaseError(error, 'updateAiSpendingCap');
}

export async function getAdvisorEnabledFlag(companyId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('company_internal_flags')
    .select('advisor_enabled')
    .eq('company_id', companyId)
    .maybeSingle();
  handleSupabaseError(error, 'getAdvisorEnabledFlag');
  return data?.advisor_enabled ?? true;
}

// ==========================================
// TOOL HISTORY (Phase 1.5)
// ==========================================
export async function getToolHistory(toolId: string): Promise<ToolHistoryEntry[]> {
  const { data, error } = await supabase
    .from('tools_history')
    .select('*')
    .eq('tool_id', toolId)
    .order('changed_at', { ascending: false })
    .limit(50);
  if (error || !data) return [];
  return data.map((row: any) => ({
    id: row.id,
    toolId: row.tool_id,
    changedAt: row.changed_at,
    fieldChanged: row.field_changed,
    oldValue: row.old_value,
    newValue: row.new_value,
  }));
}

// ==========================================
// FEATURE REQUESTS (Sprint 4)
// ==========================================
export async function getFeatureRequests(userId?: string): Promise<FeatureRequest[]> {
  const { data, error } = await supabase
    .from('feature_requests')
    .select('*')
    .order('vote_count', { ascending: false });
  if (error || !data) return [];
  let votes: Set<string> = new Set();
  if (userId) {
    const { data: voteData } = await supabase
      .from('feature_votes')
      .select('feature_request_id')
      .eq('user_id', userId);
    if (voteData) votes = new Set(voteData.map((v: any) => v.feature_request_id));
  }
  return data.map((row: any) => ({
    id: row.id,
    title: row.title,
    description: row.description ?? '',
    status: row.status ?? 'open',
    adminResponse: row.admin_response ?? undefined,
    authorName: row.author_name ?? 'Anonymous',
    userId: row.user_id,
    voteCount: row.vote_count ?? 0,
    hasVoted: votes.has(row.id),
    createdAt: row.created_at,
  }));
}

export async function addFeatureRequest(
  title: string, description: string, userId: string, authorName: string, companyId: string
): Promise<void> {
  const { error } = await supabase.from('feature_requests').insert([{
    title, description, user_id: userId, author_name: authorName, company_id: companyId,
  }]);
  handleSupabaseError(error, 'addFeatureRequest');
}

export async function voteFeatureRequest(featureId: string, userId: string, companyId: string): Promise<void> {
  const { error } = await supabase.from('feature_votes').insert([{
    feature_request_id: featureId, user_id: userId, company_id: companyId,
  }]);
  if (error && !error.message?.includes('duplicate')) handleSupabaseError(error, 'voteFeatureRequest');
}

export async function unvoteFeatureRequest(featureId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('feature_votes')
    .delete()
    .eq('feature_request_id', featureId)
    .eq('user_id', userId);
  handleSupabaseError(error, 'unvoteFeatureRequest');
}

export async function updateFeatureRequestStatus(
  id: string, status: string, adminResponse?: string
): Promise<void> {
  const updates: any = { status };
  if (adminResponse !== undefined) updates.admin_response = adminResponse;
  const { error } = await supabase.from('feature_requests').update(updates).eq('id', id);
  handleSupabaseError(error, 'updateFeatureRequestStatus');
}

export async function saveSettings(settings: AppSettings, companyId: string): Promise<AppSettings> {
  const sqlData = settingsToSql(settings);
  const row = { ...sqlData, company_id: companyId };

  const { error } = await supabase
    .from('app_settings')
    .upsert(row, { onConflict: 'company_id' });
  handleSupabaseError(error, 'saveSettings:upsert');

  return getSettings(companyId);
}
